// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React from 'react';
import { Card, Tag, Typography } from '@kintent/glide';
import { Link } from 'react-navi';
import { format } from 'date-fns';

import * as Styles from './Hero.styles';
import { Search } from '../../../search/Search';
import CustomButton from '../../../../../components/CustomButton/CustomButton';
import { Flex } from '../../../../../components/Flex';
import { getDefaultCertificationDescription } from '../../../../../lib/utils';
import { DOCUMENT_TYPE, getCertificationCardTitle, TRUST_SHARE_ACCESS_LEVELS } from '../../../../../lib/constants';
import {
  useAuthService,
  usePolicyList,
  useProgramContent,
  useTeamCertificationList,
  useTeamDocumentList,
  useTrustShareNotifications,
} from '../../../../../lib/state';

const PRIMARY_SECTION_TAG_LABEL = {
  [DOCUMENT_TYPE.CERTIFICATION]: 'Featured Certification',
  [DOCUMENT_TYPE.RESOURCE]: 'Featured Document',
  [DOCUMENT_TYPE.POLICY]: 'Featured Policy',
  [DOCUMENT_TYPE.NOTIFICATION]: 'Featured Notification',
};

const PRIMARY_SECTION_TAG_TYPE = {
  [DOCUMENT_TYPE.CERTIFICATION]: 'kelp',
  [DOCUMENT_TYPE.RESOURCE]: 'tuna',
  [DOCUMENT_TYPE.POLICY]: 'charade',
  [DOCUMENT_TYPE.NOTIFICATION]: 'matterhorn',
};

const SEARCH_PLACEHOLDER = 'Search for notifications, documents, certifications or other items';

function ListItem({ itemId, type, shouldDisplayCreatedDate }) {
  const { currentTeam } = useAuthService();
  const [polices] = usePolicyList();
  const [documents] = useTeamDocumentList(currentTeam?.id);
  const { data: allNotifications } = useTrustShareNotifications();
  const [teamCertifications] = useTeamCertificationList(currentTeam?.id);

  switch (type) {
    case DOCUMENT_TYPE.POLICY: {
      const policy = polices.find((item) => item.id === itemId);

      // Return and display nothing in case we don't find a policy
      if (!policy) return null;

      const displayDate = format(new Date(policy._metadata.createdDate), 'MMMM dd, yyyy');

      return (
        <Card
          data-testid="primary-section-list-item"
          className="primary-section-list-item"
        >
          <Flex
            direction="column"
            gap="20px"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
            >
              <Tag
                type="category"
                variant={PRIMARY_SECTION_TAG_TYPE[type]}
              >
                {PRIMARY_SECTION_TAG_LABEL[type]}
              </Tag>
              {shouldDisplayCreatedDate && (
                <Typography
                  level="9"
                  strength="60%"
                >
                  {displayDate}
                </Typography>
              )}
            </Flex>
            <Flex
              direction="column"
              gap="12px"
            >
              <Typography
                as="p"
                level="6"
                className="primary-section-list-item-title"
              >
                {policy.title}
              </Typography>
              <Typography
                level="8"
                strength="60%"
                className="primary-section-list-item-description"
              >
                {policy.description}
              </Typography>
            </Flex>
            <div className="primary-section-list-item-actions">
              <CustomButton
                as={Link}
                className="secondary"
                href={`/documents/${policy.id}?type=${DOCUMENT_TYPE.POLICY}`}
              >
                Learn More
              </CustomButton>
            </div>
          </Flex>
        </Card>
      );
    }

    case DOCUMENT_TYPE.RESOURCE: {
      const document = documents.find((item) => item.id === itemId);

      // Return and display nothing if we don't find a document
      if (!document) return null;

      const displayDate = format(new Date(document._metadata.createdDate), 'MMMM dd, yyyy');
      return (
        <Card
          data-testid="primary-section-list-item"
          className="primary-section-list-item"
        >
          <Flex
            direction="column"
            gap="20px"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
            >
              <Tag
                type="category"
                variant={PRIMARY_SECTION_TAG_TYPE[type]}
              >
                {PRIMARY_SECTION_TAG_LABEL[type]}
              </Tag>
              {shouldDisplayCreatedDate && (
                <Typography
                  level="9"
                  strength="60%"
                >
                  {displayDate}
                </Typography>
              )}
            </Flex>
            <Flex
              direction="column"
              gap="12px"
            >
              <Typography
                as="p"
                level="6"
                className="primary-section-list-item-title"
              >
                {document.displayName}
              </Typography>
              <Typography
                level="8"
                strength="60%"
                className="primary-section-list-item-description"
              >
                {document.description}
              </Typography>
            </Flex>
            <div className="primary-section-list-item-actions">
              <CustomButton
                as={Link}
                className="secondary"
                href={`/documents/${document.id}?type=${DOCUMENT_TYPE.RESOURCE}`}
              >
                Learn More
              </CustomButton>
            </div>
          </Flex>
        </Card>
      );
    }

    case DOCUMENT_TYPE.NOTIFICATION: {
      const notification = allNotifications.find((item) => item.id === itemId);

      if (!notification) return null;

      const displayDate = format(new Date(notification._metadata.createdDate), 'MMMM dd, yyyy');

      return (
        <Card
          data-testid="primary-section-list-item"
          className="primary-section-list-item"
        >
          <Flex
            direction="column"
            gap="20px"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
            >
              <Tag
                type="category"
                variant={PRIMARY_SECTION_TAG_TYPE[type]}
              >
                {PRIMARY_SECTION_TAG_LABEL[type]}
              </Tag>
              {shouldDisplayCreatedDate && (
                <Typography
                  level="9"
                  strength="60%"
                >
                  {displayDate}
                </Typography>
              )}
            </Flex>
            <Flex
              direction="column"
              gap="12px"
            >
              <Typography
                as="p"
                level="6"
                className="primary-section-list-item-title"
              >
                {notification.title}
              </Typography>
              <Typography
                level="8"
                strength="60%"
                className="primary-section-list-item-description"
              >
                {notification.description}
              </Typography>
            </Flex>
            <div className="primary-section-list-item-actions">
              <CustomButton
                as={Link}
                className="secondary"
                href={`/notifications/${notification.id}`}
              >
                Learn More
              </CustomButton>
            </div>
          </Flex>
        </Card>
      );
    }

    case DOCUMENT_TYPE.CERTIFICATION: {
      const teamCertification = teamCertifications.find((item) => item.id === itemId);

      if (!teamCertification) return null;

      const displayDate = format(new Date(teamCertification._metadata.createdDate), 'MMMM dd, yyyy');
      const title = getCertificationCardTitle(teamCertification.certification.shortName, teamCertification.subtype);
      const description = getDefaultCertificationDescription(
        currentTeam?.name,
        teamCertification.certification.shortName,
        teamCertification.status,
        teamCertification.subtype
      );
      const slug = `/certifications/${encodeURIComponent(
        `${teamCertification.certification.shortName}${teamCertification.subtype || ''}`
      )}`;

      return (
        <Card
          data-testid="primary-section-list-item"
          className="primary-section-list-item"
        >
          <Flex
            direction="column"
            gap="20px"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              gap="12px"
            >
              <Tag
                type="category"
                variant={PRIMARY_SECTION_TAG_TYPE[type]}
              >
                {PRIMARY_SECTION_TAG_LABEL[type]}
              </Tag>
              {shouldDisplayCreatedDate && (
                <Typography
                  level="9"
                  strength="60%"
                >
                  {displayDate}
                </Typography>
              )}
            </Flex>
            <Flex
              direction="column"
              gap="12px"
            >
              <Typography
                as="p"
                level="6"
                className="primary-section-list-item-title"
              >
                {title}
              </Typography>
              <Typography
                level="8"
                strength="60%"
                className="primary-section-list-item-description"
              >
                {description}
              </Typography>
            </Flex>
            <div className="primary-section-list-item-actions">
              <CustomButton
                as={Link}
                className="secondary"
                href={slug}
              >
                Learn More
              </CustomButton>
            </div>
          </Flex>
        </Card>
      );
    }
    default:
      return null;
  }
}

function PrimarySection() {
  const [{ primarySectionTitle, primarySectionContent }] = useProgramContent();

  const primarySectionContentItems = primarySectionContent?.items;
  const shouldRenderPrimarySectionContent =
    Array.isArray(primarySectionContentItems) && primarySectionContentItems.length > 0;

  return (
    <Styles.PrimarySectionWrapper data-testid="enterprise-primary-section">
      <Typography
        level="3"
        as="h4"
        color="white"
      >
        {primarySectionTitle}
      </Typography>
      <Styles.PrimarySectionGrid className="primary-section-grid">
        {shouldRenderPrimarySectionContent &&
          primarySectionContentItems.slice(0, 3).map((item) => {
            return (
              <ListItem
                key={item.id}
                type={item.type}
                itemId={item.id}
                shouldDisplayCreatedDate={item.shouldDisplayCreatedDate}
              />
            );
          })}
      </Styles.PrimarySectionGrid>
    </Styles.PrimarySectionWrapper>
  );
}

function Hero() {
  const { currentTeam, authenticatedUser } = useAuthService();
  const [{ heroHeader = null, heroSubheader = null, heroStyle = null }] = useProgramContent();

  // Extract hero header and subheader styles if they exist
  const headerWrapper = heroStyle?.wrapper ?? {};
  const heroHeaderStyle = heroStyle?.header ?? {};
  const heroSubheaderStyle = heroStyle?.subheader ?? {};

  // Permissions
  const shouldDisplaySearch =
    currentTeam.trustShareAccessLevel === TRUST_SHARE_ACCESS_LEVELS.TRANSPARENT || authenticatedUser != null;

  return (
    <Styles.Hero
      data-testid="enterprise-app-hero-section"
      style={headerWrapper}
    >
      <Styles.HeroProgramContentContainer>
        <Flex
          direction="column"
          gap="36px"
          alignItems="center"
        >
          {heroHeader && (
            <Typography
              className="hero-header"
              level="1"
              as="h1"
              style={heroHeaderStyle}
              dangerouslySetInnerHTML={{ __html: heroHeader }}
            />
          )}
          {heroSubheader && (
            <Typography
              className="hero-subheader"
              level="6"
              as="p"
              style={heroSubheaderStyle}
              dangerouslySetInnerHTML={{ __html: heroSubheader }}
            />
          )}
          {shouldDisplaySearch && (
            <Styles.SearchContainer>
              <Search placeholder={SEARCH_PLACEHOLDER} />
            </Styles.SearchContainer>
          )}
        </Flex>
        <PrimarySection />
      </Styles.HeroProgramContentContainer>
    </Styles.Hero>
  );
}

export default Hero;
